import React from 'react';
import cn from 'classnames';
import { type FileValidationError, validateFile } from './fileValidation';
import { Upload } from 'lucide-react';

export interface UploadDocumentTypeOption {
  documentId: string;
  title: string;
  maxFiles?: number;
  currentCount?: number;
}

interface UploadDropZoneProps {
  documentTypes: UploadDocumentTypeOption[];
  selectedDocumentId?: string;
  onSelectedDocumentIdChange?: (documentId: string) => void;
  onFilesAdd: (files: File[]) => void;
  readOnly?: boolean;
}

const styles = {
  wrapper: 'self-stretch pt-2 flex flex-col justify-start items-start',
  dropzone: 'self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-300 transition-all p-6 flex flex-col items-center justify-center gap-2 mt-2',
  dropzoneEnabled: 'cursor-pointer hover:outline-sky-800 hover:bg-blue-50',
  dropzoneDisabled: 'cursor-not-allowed bg-zinc-50 opacity-60',
  dropzoneActive: 'outline-sky-800 bg-blue-50',
  uploadIconWrap: 'size-8 flex items-center justify-center',
  uploadCta: 'text-center text-sky-800 text-sm font-semibold leading-5',
  uploadCtaDisabled: 'text-zinc-400',
  uploadHint: 'text-center text-neutral-500 text-xs font-normal leading-4',
  uploadHintDisabled: 'text-zinc-400',
  fileInputHidden: 'hidden',
  errorMessage: 'text-red-600 text-sm font-normal leading-5 mt-2',
};

const UploadDropZone = ({
  documentTypes,
  onFilesAdd,
  readOnly = false,
}: UploadDropZoneProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [validationError, setValidationError] = React.useState<{ message: string } | FileValidationError | null>(null);
  const isUploadDisabled = readOnly;

  const handleFiles = (selectedFiles: FileList | File[]) => {
    setValidationError(null);

    const incomingFiles = Array.from(selectedFiles);
    const validFiles: File[] = [];

    for (const file of incomingFiles) {
      const error = validateFile(file);
      if (error) {
        setValidationError(error);
        continue;
      }

      validFiles.push(file);
    }

    onFilesAdd(validFiles);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragActive(true);
    } else if (e.type === 'dragleave') {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (isUploadDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploadDisabled) {
      e.target.value = '';
      return;
    }

    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleClick = () => {
    if (isUploadDisabled) {
      return;
    }

    fileInputRef.current?.click();
  };

  return (
    <div className={cn(styles.wrapper)}>
      {!readOnly && (
        <>
          <div id='upload-dropzone'
            className={cn(
              styles.dropzone,
              isUploadDisabled ? styles.dropzoneDisabled : styles.dropzoneEnabled,
              isDragActive && !isUploadDisabled && styles.dropzoneActive
            )}
            onDragEnter={isUploadDisabled ? undefined : handleDrag}
            onDragLeave={isUploadDisabled ? undefined : handleDrag}
            onDragOver={isUploadDisabled ? undefined : handleDrag}
            onDrop={handleDrop}
            onClick={handleClick}
            role='button'
            tabIndex={isUploadDisabled ? -1 : 0}
            aria-disabled={isUploadDisabled}
            onKeyDown={(e) => {
              if (isUploadDisabled) {
                return;
              }

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }}
          >
            <div className={cn(styles.uploadIconWrap)}>
              <Upload className={cn('mt-4', isUploadDisabled ? 'text-zinc-400' : 'text-sky-800')} />
            </div>
            <div className={cn(styles.uploadCta, isUploadDisabled && styles.uploadCtaDisabled)}>
              Click to upload a file
            </div>
            <div className={cn(styles.uploadHint, isUploadDisabled && styles.uploadHintDisabled)}>
              or drag and drop- PDF, PNG, JPG, DOCX accepted
            </div>
          </div>
          <input
            ref={fileInputRef}
            type='file'
            onChange={handleChange}
            className={cn(styles.fileInputHidden)}
            aria-label='Upload file for selected document type'
            accept='.pdf,.doc,.docx,.png,.jpg,.jpeg'
            multiple
            disabled={isUploadDisabled}
          />
        </>
      )}

      {validationError && <div className={cn(styles.errorMessage)}>❌ {validationError.message}</div>}
    </div>
  );
};

export default UploadDropZone;
